import { Starfield } from './three-starfield';
import { HeroVisual } from './hero-visual';

declare const turnstile: { reset: () => void } | undefined;

interface Elements {
  header: HTMLElement | null;
  scrollIndicator: HTMLElement | null;
  projectCards: NodeListOf<HTMLElement> | null;
  principleItems: NodeListOf<HTMLElement> | null;
  contactLinks: NodeListOf<HTMLElement> | null;
  hero3DContainer: HTMLElement | null;
  heroVisualContainer: HTMLElement | null;
  mobileMenuToggle: HTMLElement | null;
  mainNav: HTMLElement | null;
  heroGrid: HTMLElement | null;
  heroMain: HTMLElement | null;
  heroVisual: HTMLElement | null;
  heroTitle: HTMLElement | null;
}

const Elements: Elements = {
  header: null,
  scrollIndicator: null,
  projectCards: null,
  principleItems: null,
  contactLinks: null,
  hero3DContainer: null,
  heroVisualContainer: null,
  mobileMenuToggle: null,
  mainNav: null,
  heroGrid: null,
  heroMain: null,
  heroVisual: null,
  heroTitle: null
};

interface ScrollState {
  lastScrollY: number;
  ticking: boolean;
}

const scrollState: ScrollState = {
  lastScrollY: 0,
  ticking: false
};

let starfield: Starfield | null = null;

let heroVisual: HeroVisual | null = null;

function init(): void {
  cacheElements();
  bindEvents();
  initScrollEffects();
  initAnimations();
  initStarfield();
  initHeroVisual();
  initMobileMenu();
  reorderHeroForMobile();
  initForms();
  initFooter();
  injectStyles();
  createParticles();
}

function initForms(): void {
  const forms = document.querySelectorAll<HTMLFormElement>('.contact-form');
  forms.forEach((form: HTMLFormElement) => {
    const loadTime = Date.now();

    form.addEventListener('submit', async function(e: SubmitEvent) {
      e.preventDefault();
      const btn = this.querySelector('button[type="submit"]');
      if (!(btn instanceof HTMLButtonElement)) return;
      const btnText = btn.querySelector('.btn-text');
      if (!(btnText instanceof HTMLElement)) return;

      const originalText = btnText.textContent ?? '';
      btnText.textContent = 'SENDING...';
      btn.disabled = true;

      const email = (this.querySelector<HTMLInputElement>('[name="email"]'))?.value ?? '';
      const message = (this.querySelector<HTMLTextAreaElement>('[name="message"]'))?.value ?? '';
      const website = (this.querySelector<HTMLInputElement>('[name="website"]'))?.value ?? '';
      const turnstileInput = this.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]');
      const token = turnstileInput?.value ?? '';

      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, message, website, token, t: loadTime }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.error ?? 'Request failed');
        }

        btnText.textContent = 'SENT!';
        btn.style.background = 'var(--accent-secondary)';
        setTimeout(() => {
          btnText.textContent = originalText;
          btn.disabled = false;
          btn.style.background = '';
          form.reset();
          if (typeof turnstile !== 'undefined') turnstile.reset();
        }, 1000);
      } catch {
        btnText.textContent = 'ERROR';
        btn.style.background = 'var(--error, #ff4444)';
        setTimeout(() => {
          btnText.textContent = originalText;
          btn.disabled = false;
          btn.style.background = '';
          if (typeof turnstile !== 'undefined') turnstile.reset();
        }, 2000);
      }
    });
  });
}

function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes float-particle {
      0%, 100% {
        transform: translateY(0) translateX(0);
        opacity: 0;
      }
      10% {
        opacity: 0.3;
      }
      90% {
        opacity: 0.3;
      }
      100% {
        transform: translateY(-100px) translateX(50px);
        opacity: 0;
      }
    }

    .project-card.in-view,
    .principle-item.in-view {
      animation: slide-up 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    @keyframes slide-up {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .project-card:nth-child(1).in-view { animation-delay: 0.1s; }
    .project-card:nth-child(2).in-view { animation-delay: 0.2s; }
    .project-card:nth-child(3).in-view { animation-delay: 0.3s; }

    .principle-item:nth-child(1).in-view { animation-delay: 0.1s; }
    .principle-item:nth-child(2).in-view { animation-delay: 0.2s; }
    .principle-item:nth-child(3).in-view { animation-delay: 0.3s; }
    .principle-item:nth-child(4).in-view { animation-delay: 0.4s; }
  `;
  document.head.appendChild(style);
}

function initFooter(): void {
  const currentYear = new Date().getFullYear();
  const footerCopyright = document.querySelector('.footer-copyright span');
  if (footerCopyright && footerCopyright.textContent?.includes('2024')) {
    footerCopyright.textContent = footerCopyright.textContent.replace('2024', String(currentYear));
  }
}

function cacheElements(): void {
  Elements.header = document.querySelector('.site-header');
  Elements.scrollIndicator = document.querySelector('.scroll-indicator');
  Elements.projectCards = document.querySelectorAll('.project-card');
  Elements.principleItems = document.querySelectorAll('.principle-item');
  Elements.contactLinks = document.querySelectorAll('.contact-link');
  Elements.hero3DContainer = document.querySelector('#hero-3d-container');
  Elements.heroVisualContainer = document.querySelector('#hero-visual-container');
  Elements.mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
  Elements.mainNav = document.querySelector('.main-nav');
  Elements.heroGrid = document.querySelector('.hero-grid');
  Elements.heroMain = document.querySelector('.hero-main');
  Elements.heroVisual = document.querySelector('.hero-visual');
  Elements.heroTitle = document.querySelector('.hero-title');
}

function bindEvents(): void {
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
}

function onScroll(): void {
  scrollState.lastScrollY = window.scrollY;

  if (!scrollState.ticking) {
    window.requestAnimationFrame(updateScrollEffects);
    scrollState.ticking = true;
  }
}

function onResize(): void {
  updateScrollEffects();
  reorderHeroForMobile();
}

function reorderHeroForMobile(): void {
  const isMobileOrTablet = window.innerWidth <= 1160;
  
  if (!Elements.heroVisual || !Elements.heroMain || !Elements.heroTitle || !Elements.heroGrid) {
    return;
  }
  
  if (isMobileOrTablet) {
    // Check if hero-visual is already inside hero-main
    if (Elements.heroVisual.parentElement !== Elements.heroMain) {
      // Insert hero-visual after hero-title
      Elements.heroMain.insertBefore(Elements.heroVisual, Elements.heroTitle.nextSibling);
    }
  } else {
    // On desktop, ensure hero-visual is back in hero-grid (outside hero-main)
    if (Elements.heroVisual.parentElement === Elements.heroMain) {
      Elements.heroGrid.insertBefore(Elements.heroVisual, Elements.heroMain);
    }
  }
}

function updateScrollEffects(): void {
  const scrollY = scrollState.lastScrollY;

  if (Elements.header) {
    if (scrollY > 100) {
      Elements.header.style.borderBottomColor = 'var(--border-accent)';
    } else {
      Elements.header.style.borderBottomColor = 'var(--border-default)';
    }
  }

  if (Elements.scrollIndicator) {
    if (scrollY > 300) {
      Elements.scrollIndicator.style.opacity = '0';
      Elements.scrollIndicator.style.pointerEvents = 'none';
    } else {
      Elements.scrollIndicator.style.opacity = '1';
      Elements.scrollIndicator.style.pointerEvents = 'auto';
    }
  }

  scrollState.ticking = false;
}

function initScrollEffects(): void {
  const observerOptions: IntersectionObserverInit = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
      }
    });
  }, observerOptions);

  if (Elements.projectCards) {
    Elements.projectCards.forEach((card: HTMLElement) => observer.observe(card));
  }
  if (Elements.principleItems) {
    Elements.principleItems.forEach((item: HTMLElement) => observer.observe(item));
  }
}

function initAnimations(): void {
  initGlitchEffect();
  initMarqueeSpeed();
  initHoverEffects();
  initSlideshows();
}

function initStarfield(): void {
  const container = document.querySelector('.hero-section');
  if (container instanceof HTMLElement) {
    starfield = new Starfield(container, {
      starCount: 8000,
      speed: 1.0,
      bloomStrength: 0.6
    });
  }
}

function initHeroVisual(): void {
  if (Elements.heroVisualContainer instanceof HTMLElement) {
    heroVisual = new HeroVisual(Elements.heroVisualContainer);
  }
}

function initMobileMenu(): void {
  if (!Elements.mobileMenuToggle || !Elements.mainNav) return;

  let isMenuOpen = false;

  Elements.mobileMenuToggle.addEventListener('click', () => {
    isMenuOpen = !isMenuOpen;
    
    if (isMenuOpen) {
      Elements.mainNav!.classList.add('mobile-menu-open');
      Elements.mobileMenuToggle!.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    } else {
      Elements.mainNav!.classList.remove('mobile-menu-open');
      Elements.mobileMenuToggle!.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });

  // Close menu when clicking on navigation links
  const navLinks = Elements.mainNav!.querySelectorAll('a[href^="#"]');
  navLinks.forEach(link => {
    link.addEventListener('click', () => {
      if (isMenuOpen) {
        isMenuOpen = false;
        Elements.mainNav!.classList.remove('mobile-menu-open');
        Elements.mobileMenuToggle!.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    });
  });

  // Close menu when clicking outside
  document.addEventListener('click', (e) => {
    if (isMenuOpen &&
        Elements.mainNav &&
        Elements.mobileMenuToggle &&
        !Elements.mainNav.contains(e.target as Node) &&
        !Elements.mobileMenuToggle.contains(e.target as Node)) {
      isMenuOpen = false;
      Elements.mainNav!.classList.remove('mobile-menu-open');
      Elements.mobileMenuToggle!.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });

  // Close menu on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isMenuOpen) {
      isMenuOpen = false;
      Elements.mainNav!.classList.remove('mobile-menu-open');
      Elements.mobileMenuToggle!.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });
}

function initGlitchEffect(): void {
  const glitchElements = document.querySelectorAll('.glitch-text');

  glitchElements.forEach((el: Element) => {
    if (!(el instanceof HTMLElement)) return;
    let timeout: ReturnType<typeof setTimeout>;

    el.addEventListener('mouseenter', () => {
      const duration = 50;
      let iterations = 0;
      const maxIterations = 6;
      const text = el.dataset.text ?? '';

    function glitch(): void {
        if (iterations >= maxIterations) {
          el.textContent = text;
          return;
        }

        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*';
        let result = '';

        for (let i = 0; i < text.length; i++) {
          if (Math.random() > 0.7) {
            result += chars[Math.floor(Math.random() * chars.length)];
          } else {
            result += text[i];
          }
        }

        el.textContent = result;
        iterations++;

        timeout = setTimeout(glitch, duration);
      }

      glitch();
    });

    el.addEventListener('mouseleave', () => {
      clearTimeout(timeout);
      el.textContent = el.dataset.text ?? '';
    });
  });
}

function initMarqueeSpeed(): void {
  const marquee = document.querySelector('.marquee-track');

  if (marquee instanceof HTMLElement) {
    marquee.addEventListener('mouseenter', () => {
      marquee.style.animationPlayState = 'paused';
    });

    marquee.addEventListener('mouseleave', () => {
      marquee.style.animationPlayState = 'running';
    });
  }
}

function initHoverEffects(): void {
  if (Elements.projectCards) {
    Elements.projectCards.forEach((card: HTMLElement) => {
      card.addEventListener('mouseenter', function(this: HTMLElement) {
        this.style.setProperty('--card-offset', '4px');
      });

      card.addEventListener('mouseleave', function(this: HTMLElement) {
        this.style.setProperty('--card-offset', '0px');
      });
    });
  }

  const visualFrame = document.querySelector('.visual-frame');
  if (visualFrame instanceof HTMLElement) {
    visualFrame.addEventListener('mousemove', function(this: HTMLElement, e: MouseEvent) {
      const rect = this.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;

      this.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    });

    visualFrame.addEventListener('mouseleave', function(this: HTMLElement) {
      this.style.transform = 'perspective(1000px) rotateX(0) rotateY(0)';
    });
  }
}

function initSlideshows(): void {
  const slideshows = document.querySelectorAll('[data-slideshow]');

  slideshows.forEach((slideshowElement: Element) => {
    if (!(slideshowElement instanceof HTMLElement)) return;
    const slideshow = slideshowElement;
    const track = slideshow.querySelector('[data-slideshow-track]');
    const indicators = slideshow.querySelectorAll('[data-slideshow-indicators] [data-index]');
    const images = slideshow.querySelectorAll('.slideshow-image');

    if (!track || images.length === 0) return;

    let currentIndex = 0;
    const totalSlides = images.length;
    let autoPlayInterval: ReturnType<typeof setInterval> | undefined;
    const slideDuration = 2500;
    const transitionDuration = 800;

    if (images.length === 0) return;

    const firstImage = images[0];
    if (firstImage instanceof HTMLElement) {
      firstImage.classList.add('active');
    }

    function showSlide(index: number): void {
      const currentImage = images[currentIndex];
      const nextImage = images[index];
      const currentIndicator = slideshow.querySelector('.slideshow-indicator.active');
      const nextIndicator = indicators[index];

      if (currentImage instanceof HTMLElement) {
        currentImage.classList.remove('active');
        currentImage.classList.add('prev');
      }

      if (nextImage instanceof HTMLElement) {
        nextImage.classList.remove('prev');
        nextImage.classList.add('active');
      }

      if (currentIndicator) currentIndicator.classList.remove('active');
      if (nextIndicator) nextIndicator.classList.add('active');

      if (currentImage instanceof HTMLElement) {
        setTimeout(() => {
          currentImage.classList.remove('prev');
        }, transitionDuration);
      }

      currentIndex = index;
    }

    function nextSlide(): void {
      const nextIndex = (currentIndex + 1) % totalSlides;
      showSlide(nextIndex);
    }

    function startAutoPlay(): void {
      autoPlayInterval = setInterval(nextSlide, slideDuration);
    }

    function stopAutoPlay(): void {
      if (autoPlayInterval !== undefined) {
        clearInterval(autoPlayInterval);
      }
    }

    indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        if (index !== currentIndex) {
          stopAutoPlay();
          showSlide(index);
          startAutoPlay();
        }
      });
    });

    slideshow.addEventListener('mouseenter', () => {
      stopAutoPlay();
    });

    slideshow.addEventListener('mouseleave', () => {
      startAutoPlay();
    });

    let touchStartX = 0;
    let touchEndX = 0;

    slideshow.addEventListener('touchstart', (e: TouchEvent) => {
      touchStartX = e.changedTouches[0]?.screenX ?? 0;
      stopAutoPlay();
    }, { passive: true });

    slideshow.addEventListener('touchend', (e: TouchEvent) => {
      touchEndX = e.changedTouches[0]?.screenX ?? 0;
      handleSwipe();
      startAutoPlay();
    }, { passive: true });

    function handleSwipe(): void {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          const nextIndex = (currentIndex + 1) % totalSlides;
          showSlide(nextIndex);
        } else {
          const prevIndex = (currentIndex - 1 + totalSlides) % totalSlides;
          showSlide(prevIndex);
        }
      }
    }

    startAutoPlay();
  });
}

function smoothScrollTo(targetId: string): void {
  const target = document.querySelector(targetId);
  if (target instanceof HTMLElement) {
    const offsetTop = target.offsetTop - 100;
    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth'
    });
  }
}

document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener('click', function(e: MouseEvent) {
    const href = this.getAttribute('href');
    if (href && href.length > 1 && href.startsWith('#')) {
      e.preventDefault();
      smoothScrollTo(href);
    }
  });
});

function createParticles(): void {
  const hero = document.querySelector('.hero-section');
  if (!hero) return;

  for (let i = 0; i < 20; i++) {
    const particle = document.createElement('div');
    particle.className = 'particle';
    particle.style.cssText = `
      position: absolute;
      width: 2px;
      height: 2px;
      background: var(--accent-secondary);
      opacity: 0.3;
      pointer-events: none;
      left: ${Math.random() * 100}%;
      top: ${Math.random() * 100}%;
      animation: float-particle ${5 + Math.random() * 10}s ease-in-out infinite;
      animation-delay: ${Math.random() * 5}s;
    `;
    hero.appendChild(particle);
  }
}

window.addEventListener('beforeunload', () => {
  if (starfield) {
    starfield.dispose();
  }
  if (heroVisual) {
    heroVisual.dispose();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
